import { useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { css, useTheme } from '@emotion/react'
import { matchPath } from 'react-router'
import useMediaQuery from '@mui/material/useMediaQuery'

import {
  Button,
  Loading,
  LoadingOverlay,
  RetirementIncomeDonut,
  RetirementIncomeValues,
} from '../../../components'
import { incomeGoalLayout } from '../../../components/RetirementIncomeDonut'
import { TABLET_VIEWPORT } from '../../../guided-toolbox/utils/breakpoints'
import { DEFAULT_RETIREMENT_INCOME_GOAL_PCT } from '../../../models/Person'
import { projectedIncomeValues, withRouterProps, formatPercentLabel } from '../../../utils'
import CouldYouLiveHelp from '../IncomeReplacement/CouldYouLiveHelp'
import IncomeReplacementDrawer from '../IncomeReplacement/IncomeReplacementDrawer'
import {
  Section,
  IncomeBody,
  IncomeLockup,
  ChartCell,
  ValuesCell,
  CouldYouLive,
  ChangeIncomeButton,
  ErrorMessage,
} from './GoalsDashboardStyle'

const saveAlertMessage = saveStatus => {
  if (saveStatus === 'saveFailed') {
    return "We couldn't save your change, please try again."
  }

  if (saveStatus === 'resultsStale') {
    return "Your change was saved, but we couldn't update your results, please try again."
  }

  return null
}

const RetirementIncomeSection = ({ location, navigate, store }) => {
  const { baseCase, getBaseCase, person, updatePerson } = store
  const theme = useTheme()
  const isMobileOrTablet = useMediaQuery(TABLET_VIEWPORT)
  const [showValueAtRetirement, setShowValueAtRetirement] = useState(false)
  const [status, setStatus] = useState(baseCase ? 'done' : 'loading')
  const [saveStatus, setSaveStatus] = useState('idle')
  const [attemptedPercent, setAttemptedPercent] = useState(null)

  useEffect(() => {
    if (baseCase) {
      setStatus('done')
      return undefined
    }

    // Ignore this request if another fetch supplies the base case first;
    // its later rejection must not replace loaded data with an error.
    let active = true

    getBaseCase()
      .then(() => {
        if (active) {
          setStatus('done')
        }
      })
      .catch(err => {
        console.error(err)

        if (active) {
          setStatus('error')
        }
      })

    return () => {
      active = false
    }
  }, [baseCase, getBaseCase])

  // The route is the source of truth for the drawer's open state.
  const isDrawerOpen = Boolean(
    matchPath({ path: '/goals/replace-income', end: true }, location.pathname)
  )
  const closeDrawer = () => navigate('/goals', { replace: true })

  const recalculate = async () => {
    setSaveStatus('saving')

    try {
      await getBaseCase()
      setSaveStatus('idle')
    } catch (err) {
      console.error(err)
      setSaveStatus('resultsStale')
    }
  }

  const handleSave = async percentOfIncome => {
    // Navigate first so the overlay does not appear over the drawer.
    navigate('/goals', { replace: true })
    setSaveStatus('saving')
    setAttemptedPercent(percentOfIncome)

    // A failed save changes nothing; a failed recalculation leaves stale results.
    try {
      await updatePerson({ id: person.id, retirementIncomeGoalPct: percentOfIncome })
      setAttemptedPercent(null)
    } catch (err) {
      console.error(err)
      setSaveStatus('saveFailed')
      return
    }

    await recalculate()
  }

  const retry = () => {
    if (saveStatus === 'saveFailed') {
      handleSave(attemptedPercent)
    } else {
      recalculate()
    }
  }

  if (status === 'loading') {
    return (
      <Section>
        <Loading />
      </Section>
    )
  }

  if (status === 'error' || !baseCase) {
    return (
      <Section>
        <ErrorMessage>
          We couldn't load your retirement income right now. Please try again later.
        </ErrorMessage>
      </Section>
    )
  }

  const goalPercent = person?.retirementIncomeGoalPct ?? DEFAULT_RETIREMENT_INCOME_GOAL_PCT
  const values = projectedIncomeValues({ retirementIncomeGoalPct: goalPercent, baseCase })
  const saveAlert = saveAlertMessage(saveStatus)

  const chart = (
    <ChartCell>
      <RetirementIncomeDonut
        centerValue={`${formatPercentLabel(goalPercent)}%`}
        monthlyLabel='of current income'
        replacementPercent={goalPercent}
        /* The plan arc already shows the goal, so disable the separate goal band. */
        goalPercent={0}
        layout={incomeGoalLayout(isMobileOrTablet)}
        colors={{ digits: theme.primaryColor, monthly: theme.primaryColor }}
      />
    </ChartCell>
  )

  const valuesCell = (
    <ValuesCell>
      <CouldYouLive>
        Could you live on this amount?
        <CouldYouLiveHelp />
      </CouldYouLive>

      <RetirementIncomeValues
        values={values}
        compact={isMobileOrTablet}
        emphasizeToday
        expanded={showValueAtRetirement}
        onToggle={() => setShowValueAtRetirement(shown => !shown)}
        /* The narrow design has no room for the help icon beside the label. */
        showAtRetirementHelp={!isMobileOrTablet}
      />
    </ValuesCell>
  )

  const changeButton = (
    <ChangeIncomeButton type='button' onClick={() => navigate('/goals/replace-income')}>
      {isMobileOrTablet ? 'Change retirement income' : 'Change'}
    </ChangeIncomeButton>
  )

  return (
    <Section>
      {/* Ordered per breakpoint: mobile/tablet puts the values between the gauge and its button. */}
      <IncomeBody>
        {isMobileOrTablet ? (
          <>
            {chart}
            {valuesCell}
            {changeButton}
          </>
        ) : (
          <>
            <IncomeLockup>
              {chart}
              {changeButton}
            </IncomeLockup>
            {valuesCell}
          </>
        )}
      </IncomeBody>

      {saveAlert && (
        <>
          <ErrorMessage role='alert'>{saveAlert}</ErrorMessage>
          <Button
            variant='secondary'
            onClick={retry}
            css={css`
              align-self: flex-start;
            `}>
            Try again
          </Button>
        </>
      )}

      <IncomeReplacementDrawer
        active={isDrawerOpen}
        toggleDrawer={closeDrawer}
        handleSave={handleSave}
      />

      {saveStatus === 'saving' && <LoadingOverlay label='Calculating results' />}
    </Section>
  )
}

export default withRouterProps(inject('store')(observer(RetirementIncomeSection)))
